import axios from "axios";
import normalize from "Utils/normalizer";
import errorHandler from "Utils/error-handler";
import { getAccessToken } from "Helpers/session";
import { toLower, omitBy, isEmpty, isNil, map, some, isArray } from "lodash";
import { DefaultFilter, Pagination, Sorting } from "../types/common.types";
import { CustomerLocationFormType } from "Types/customer_location.types";

const url = `${process.env.API_URL}/${process.env.API_VER}`;

interface Filters extends DefaultFilter {
  orFields?: string;
  tempDelete?: boolean;
}

export const createCustomerLocation = async (data: CustomerLocationFormType) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/location/create`,
      data: normalize.model.customer_location_details(data),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createCustomerLocation(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateCustomerLocation = async (data: CustomerLocationFormType) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/location/update`,
      data: normalize.model.customer_location_details(data),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateCustomerLocation(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchCustomerLocationList = async (
  filters: Filters,
  orgID: string[],
  pagination?: Pagination,
  sorting?: Sorting
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/location/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
          orgID: isArray(orgID) ? orgID : [orgID],
        },
        isNil
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data.locations;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchCustomerLocationList(filters, orgID, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const deleteCustomerLocation = async (locationID: string, tempDeletedReason: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/location/delete`,
      data: { locationID, tempDeletedReason },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await deleteCustomerLocation(locationID, tempDeletedReason);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const restoreCustomerLocation = async (locationID: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/location/restore/update`,
      data: { locationID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await restoreCustomerLocation(locationID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchCustomerLocation = async (locationID: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/location/fetch`,
      data: { locationID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return normalize.response.customer_location_details(response.data.location);
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchCustomerLocation(locationID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateOperationalHoursForIndividuals = async (orgIDs: any, operationalHours: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/location/individual_customer/working_hours/update`,
      data: {
        orgIDs: orgIDs,
        workingHours: normalize.model.individual_operational_hours(operationalHours),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await updateOperationalHoursForIndividuals(orgIDs, operationalHours);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};
